import React, { useRef, useState } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EmojiPickerPopover from './EmojiPickerPopover';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import { SlackService } from 'service/slack.service';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import zeplinColor from 'theme/Color';

interface SlackReplyBoxProps {
  onClose: () => void;
  onSend?: (text: string) => void;
  channelId?: string;
  threadTs?: string;
}

const SlackReplyBox = ({ onClose, onSend, channelId, threadTs }: SlackReplyBoxProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);
  const activeEditingLinkRef = useRef<HTMLAnchorElement | null>(null);
  const savedRange = useRef<Range | null>(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const updateActiveFormats = () => {
    const active = new Set<string>();
    ['bold', 'italic', 'strikeThrough', 'insertOrderedList', 'insertUnorderedList'].forEach(
      (cmd) => {
        if (document.queryCommandState(cmd)) active.add(cmd);
      },
    );
    setActiveFormats(active);
  };

  const execFormat = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    updateActiveFormats();
  };

  const handleLink = () => {
    saveSelection();
    const sel = window.getSelection();
    const selectedText = sel && sel.rangeCount > 0 ? sel.toString() : '';
    setLinkText(selectedText);
    setLinkUrl('');
    setLinkModalOpen(true);
  };

  const handleLinkModalClose = () => {
    setLinkModalOpen(false);
    setLinkText('');
    setLinkUrl('');
    setIsEditingExistingLink(false);
    activeEditingLinkRef.current = null;
  };

  const handleLinkSave = () => {
    const trimmedUrl = linkUrl.trim();
    const trimmedText = linkText.trim() || trimmedUrl;

    if (!trimmedUrl) return;

    if (isEditingExistingLink && activeEditingLinkRef.current) {
      // SCENARIO A: Updating an existing link in place
      activeEditingLinkRef.current.setAttribute('href', trimmedUrl);
      activeEditingLinkRef.current.innerText = trimmedText;
    } else {
      // SCENARIO B: Inserting a completely new link at cursor
      editorRef.current?.focus();
      restoreSelection();
      const anchorHtml = `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">${trimmedText}</a>`;

      document.execCommand('insertHTML', false, anchorHtml);
    }

    handleInput();
    handleLinkModalClose();
  };
  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // If user clicked an anchor tag, trigger the edit workflow
    if (target.tagName === 'A') {
      e.preventDefault();
      const anchor = target as HTMLAnchorElement;

      activeEditingLinkRef.current = anchor;
      setLinkText(anchor.innerText);
      setLinkUrl(anchor.getAttribute('href') || '');
      setIsEditingExistingLink(true); // Flag that we are updating, not creating
      setLinkModalOpen(true);
    }
  };

  const toolbarItems = [
    {
      icon: <FormatBoldIcon sx={{ fontSize: 22 }} />,
      label: 'Bold',
      command: 'bold',
      action: () => execFormat('bold'),
    },
    {
      icon: <FormatItalicIcon sx={{ fontSize: 22 }} />,
      label: 'Italic',
      command: 'italic',
      action: () => execFormat('italic'),
    },
    {
      icon: <StrikethroughSIcon sx={{ fontSize: 22 }} />,
      label: 'Strikethrough',
      command: 'strikeThrough',
      action: () => execFormat('strikeThrough'),
    },
    { divider: true },
    {
      icon: <FormatListNumberedIcon sx={{ fontSize: 22 }} />,
      label: 'Ordered list',
      command: 'insertOrderedList',
      action: () => execFormat('insertOrderedList'),
    },
    {
      icon: <FormatListBulletedIcon sx={{ fontSize: 22 }} />,
      label: 'Bullet list',
      command: 'insertUnorderedList',
      action: () => execFormat('insertUnorderedList'),
    },
    { divider: true },
    {
      icon: <img src='/assets/add-link.svg' alt='link' style={{ width: '22px', height: '22px' }} />,
      label: 'Link',
      command: '',
      action: handleLink,
    },
  ];

  const handleInput = () => {
    const text = editorRef.current?.innerText ?? '';
    setIsEmpty(text.trim() === '');
  };

  const handleSend = async () => {
    const text = editorRef.current?.innerText ?? '';
    if (!text.trim()) return;
    const html = editorRef.current?.innerHTML?.toString() ?? '';

    if (channelId && threadTs) {
      setSending(true);
      try {
        await SlackService.replyToSlackMessage(channelId, threadTs, html);
      } finally {
        setSending(false);
      }
    }
    onSend?.(html);
    if (editorRef.current) editorRef.current.innerHTML = '';
    setIsEmpty(true);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+Enter or Cmd+Enter → send
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
      return;
    }
  };

  const insertEmojiAtCursor = (emojiNative: string) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand('insertText', false, emojiNative);
    handleInput();
  };

  return (
    <Box
      sx={{
        border: `1px solid ${zeplinColor.Neutral5}`,
        backgroundColor: zeplinColor.Neutral1,
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          padding: '4px 8px',
          borderBottom: `1px solid ${zeplinColor.Neutral6}`,
          backgroundColor: zeplinColor.Neutral3,
          flexWrap: 'wrap',
        }}
      >
        {toolbarItems.map((item, idx) =>
          item.divider ? (
            <Box
              key={`divider-${idx}`}
              sx={{
                width: '1px',
                height: '16px',
                backgroundColor: zeplinColor.Neutral5,
                mx: '2px',
              }}
            />
          ) : (
            <Tooltip key={item.label} title={item.label!} placement='top' arrow>
              <IconButton
                size='large'
                onMouseDown={(e) => {
                  e.preventDefault(); // keep focus + selection in editor
                  item.action?.();
                }}
                sx={{
                  padding: '4px',
                  color: zeplinColor.BasicBlack,
                  borderRadius: '4px',
                  backgroundColor:
                    item.command && activeFormats.has(item.command)
                      ? zeplinColor.Neutral5
                      : 'transparent',
                  '&:hover': {
                    backgroundColor: zeplinColor.Neutral5,
                    color: zeplinColor.BasicBlack,
                  },
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          ),
        )}

        {/* Emoji button — saves selection then opens picker */}
        <Tooltip title='Emoji' placement='top' arrow>
          <IconButton
            size='large'
            sx={{
              padding: '4px',
              color: zeplinColor.BasicBlack,
              borderRadius: '4px',
              '&:hover': { backgroundColor: zeplinColor.Neutral3 },
            }}
            onMouseDown={() => saveSelection()}
            onClick={(e) => setPickerAnchor(e.currentTarget)}
          >
            <EmojiEmotionsOutlinedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        {/* Spacer + close button */}
        <Box sx={{ flex: 1 }} />
        <img
          src='/assets/cancel-circle.svg'
          alt='close'
          style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
          onClick={onClose}
        />
      </Box>

      {/* ContentEditable rich-text editor */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onClick={handleEditorClick}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onKeyUp={updateActiveFormats}
        onMouseUp={updateActiveFormats}
        onSelect={updateActiveFormats}
        data-placeholder='Reply...'
        sx={{
          padding: '8px 12px',
          fontSize: '13px',
          lineHeight: 1.5,
          color: zeplinColor.BasicBlack,
          flex: 1,
          minHeight: '60px',
          overflowY: 'auto',
          outline: 'none',
          wordBreak: 'break-word',
          '&:empty::before': {
            content: 'attr(data-placeholder)',
            color: zeplinColor.Neutral7,
            pointerEvents: 'none',
          },
          '& ul, & ol': { paddingLeft: '20px', margin: '4px 0' },
          '& a': { color: '#1264a3', textDecoration: 'underline' },
        }}
      />

      {/* Bottom bar — send */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '4px 8px',
          position: 'absolute',
          top: '45px',
          right: '0px',
        }}
      >
        <Tooltip title='Send (Ctrl+Enter)' placement='top' arrow>
          <img
            src='/assets/send-slack.svg'
            alt='send'
            style={{
              width: '28px',
              height: '28px',
              cursor: !isEmpty && !sending ? 'pointer' : 'default',
              opacity: !isEmpty && !sending ? 1 : 0.4,
            }}
            onClick={handleSend}
          />
        </Tooltip>
      </Box>

      <EmojiPickerPopover
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        onEmojiSelect={(emoji: any) => insertEmojiAtCursor(emoji.native ?? '')}
      />

      {/* Add Link modal */}
      <CustomModal handleClose={handleLinkModalClose} open={linkModalOpen}>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,
              width: '510px',
              boxShadow: '0px 4px 24px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                Add Link
              </Typography>
              <img
                src='/assets/close-icon.svg'
                width={24}
                height={24}
                alt='close'
                style={{ cursor: 'pointer' }}
                onClick={handleLinkModalClose}
              />
            </Box>

            {/* Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <TextField
                label='Text'
                variant='outlined'
                fullWidth
                size='small'
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder='Display text'
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: zeplinColor.BasicBlack,
                  },
                  '& label.Mui-focused': { color: zeplinColor.BasicBlack },
                }}
              />
              <TextField
                label='Link'
                variant='outlined'
                fullWidth
                size='small'
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder='https://'
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && linkUrl.trim()) handleLinkSave();
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: zeplinColor.BasicBlack,
                  },
                  '& label.Mui-focused': { color: zeplinColor.BasicBlack },
                }}
              />
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
              <CustomButton
                disabled={false}
                onClick={handleLinkModalClose}
                variant='outlined'
                color='primary'
                buttonVariant='responsiveButtonMedium'
                style={{
                  borderRadius: '10px',
                  height: '40px',
                  border: `1px solid ${zeplinColor.BasicBlack}`,
                  '&:hover': { backgroundColor: zeplinColor.Neutral3 },
                }}
                textStyle={{ color: zeplinColor.BasicBlack }}
                text='Cancel'
              />
              <CustomButton
                disabled={!linkUrl.trim()}
                onClick={handleLinkSave}
                variant='contained'
                color='primary'
                buttonVariant='responsiveButtonMedium'
                style={{
                  borderRadius: '10px',
                  height: '40px',
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text='Save'
              />
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default SlackReplyBox;
